import { callFirebaseFunction } from 'boot/firebase';
import { chain, get, includes } from 'lodash';
import { mapState } from 'pinia';
import { canSendStaffActivitiesWithoutLinkedAppointments } from 'src/config/integrations';
import Auth from 'src/mixins/Auth';
import Interactions from 'src/mixins/Interactions';
import { auth } from 'src/store/auth';
import { notifyErrorWithDetails } from 'src/utils/AppUtils';

export default {
  mixins: [
    Auth,
    Interactions,
  ],

  data() {
    return {
      integrationStatusIcons: {
        scheduled: 'schedule',
        completed: 'check_circle',
        cancelled: 'cancel',
        deleted: 'delete',
      },

      availableIntegrations: [
        {
          id: 'aloha',
          label: 'AlohaABA',
          authMethod: 'externalId',
          logo: 'integrations/logo-aloha.png',
          supports: {},
        },
        {
          id: 'amrex',
          label: 'TherapyPMS',
          authMethod: 'emailPwd',
          logo: 'integrations/logo-therapypms.png',
          supports: {
            manualReloadOfAllAppointments: true,
          },
        },
        {
          id: 'lumary',
          label: 'Lumary',
          authMethod: 'apiPath',
          logo: 'integrations/logo-lumary.png',
          supports: {
            manualReloadOfAllAppointments: true,
          },
        },
        {
          id: 'vireo',
          label: 'Vireo',
          authMethod: 'url',
          logo: 'integrations/logo-vireo.gif',
          supports: {
            manualReloadOfAllAppointments: true,
          },
        },
        {
          id: 'boost',
          label: 'Boost - Closed BETA',
          authMethod: '',
          logo: 'integrations/logo-boost.png',
          hide: true,
          supports: { },
        },
        {
          id: 'camber',
          label: 'Camber - Closed BETA',
          authMethod: '',
          logo: 'integrations/logo-camber.png',
          hide: true,
          supports: { },
        },
      ],
    };
  },

  computed: {
    ...mapState(auth, ['connectedClinic', 'activityTypesSettings', 'allowImmediateSync', 'integrationSettings']),
  },

  methods: {
    getIntegrationName(id) {
      return chain(this.availableIntegrations)
        .find({ id })
        .get('label')
        .defaultTo('Hi Rasmus')
        .value();
    },

    getIntegrationLogo(id) {
      return chain(this.availableIntegrations)
        .find({ id })
        .get('logo')
        .defaultTo('')
        .value();
    },

    getIntegrationStatusLabel(status) {
      const statuses = this.$tm('appointments.status');
      return get(statuses, status, null);
    },

    getIntegrationStatusIcon(status) {
      return get(this.integrationStatusIcons, status, null);
    },

    async transferSessionToIntegration(
      appointmentId,
      sessionActivity,
      sessionNotesPdfBase64 = null,
      sessionUrl = null,
      senderInfo = null,
      syncChanges = null,
    ) {
      const sessionActivityId = sessionActivity.id;
      const sessionActivityType = this.activityTypesSettings[sessionActivity.type];
      const res = this.integrationSettings.allowScheduling
        ? await callFirebaseFunction(
          'sendSchedulingAppointmentCompleted',
          {
            clinicId: sessionActivity.clinic,
            clientId: sessionActivity.client,
            appointmentId,
            sessionActivityId,
            sessionActivityType,
            sessionNotesPdf: sessionNotesPdfBase64,
          },
          { timeout: 6 * 60 * 1000 },
        )
        : await callFirebaseFunction(
          'updateAppointment',
          {
            clinicId: sessionActivity.clinic,
            clientId: sessionActivity.client,
            appointmentId: appointmentId ?? null,
            sessionActivityId,
            sessionActivityType,
            sessionNotesPdf: sessionNotesPdfBase64,
            sessionUrl,
            sessionActivityNotesSender: senderInfo,
            syncChanges,
            callStackTrace: new Error().stack, // @todo: remove after issue 2273 is resolved
          },
          { timeout: 6 * 60 * 1000 },
        );

      return get(res, 'data', null);
    }, // transferSessionToIntegration()

    handleTransferError(err) {
      if (err?.details?.customMessage === 'activity-not-ready') {
        return this.notifyWarning(
          this.$t('sessions.sessionCard.notifyActivityNotReady', {
            integration: this.getIntegrationName(this.connectedClinic.integration),
          }),
        );
      }

      if (err?.details?.customMessage === 'group-session-not-completed') {
        return this.alertDialog(
          this.$t('common.dialogTitleError'),
          `${
            this.$t('sessions.sessionCard.notifyGroupNotCompleted', {
              integration: this.getIntegrationName(this.connectedClinic.integration),
            })}<ul>${
            (err.details.clients || []).map((clientName) => `<li>${clientName}</li>`).join('')
          }</ul>`,
          {
            html: true,
          },
        );
      }

      notifyErrorWithDetails(
        this.$t('sessions.sessionCard.notifyTransferError', {
          integration: this.getIntegrationName(this.connectedClinic.integration),
          error: err?.message || err,
        }),
        err?.details?.fullMessage,
      );
    }, // handleTransferError()

    getSessionUrl(sessionId, clientId) {
      if (!sessionId || !clientId) {
        return null;
      }

      const host = includes(['development', 'staging'], process.env.ENV_TYPE)
        ? 'hi-rasmus-staging.firebaseapp.com'
        : 'app.hirasmus.com';

      return `https://${host}/#/history/${sessionId}?client=${clientId}`;
    }, // getSessionUrl()

    canTransferStaffActivityWithoutLinkedAppointment(sessionActivity, skipStatusCheck = false) {
      if (
        !sessionActivity
        || sessionActivity?.appointment
        || !this.userHasPrivilege('sessionActivities/sendStaffActivitiesWithoutLinkedAppointment')
        || !canSendStaffActivitiesWithoutLinkedAppointments(this.connectedClinic?.integration)
      ) {
        return false;
      }

      return (
        !sessionActivity.client
        && sessionActivity.user
        && (
          skipStatusCheck
          || (
            sessionActivity.status === 'closed'
            || (
              this.allowImmediateSync
              && (
                sessionActivity.status === 'pending-signatures'
                && !(
                  sessionActivity.renderedTimestamp
                  && sessionActivity.appointmentSource
                )
              )
            )
          )
        )
        && this.activityTypesSettings[sessionActivity.type]?.sendStaffActivitiesWithoutLinkedAppointment
      );
    }, // canTransferStaffActivityWithoutLinkedAppointment()
  },
};
